import { JobRequestMessageV1 } from "@/lib/store/Realtime/message.types";
import { ToothData } from "@/dentconnect/src/pages/job-request/types";
import "../fonts.css";

export interface JobRequestV1SubmissionTemplateProps {
	content: JobRequestMessageV1;
	senderOrgName?: string;
	files?: string[];
}

export const JobRequestV1SubmissionTemplate: React.FC<
	JobRequestV1SubmissionTemplateProps
> = ({ content, senderOrgName, files = [] }) => {
	// Helper to render odontogram data
	const renderOdontogram = () => {
		if (
			!content.odontogram_teeth ||
			content.odontogram_teeth.length === 0
		) {
			return null;
		}

		const teethData = content.odontogram_teeth as ToothData[];

		// Get all field labels from the first tooth with values
		const firstToothWithValues = teethData.find(
			(tooth) => tooth.values && Object.keys(tooth.values).length > 0
		);
		const fieldLabels = firstToothWithValues
			? Object.keys(firstToothWithValues.values)
			: [];

		// Filter teeth that have actual values
		const teethWithValues = teethData.filter(
			(tooth) => tooth.values && Object.keys(tooth.values).length > 0
		);

		if (teethWithValues.length === 0) {
			// Just show selected teeth numbers
			return (
				<div style={{ marginBottom: "12px", pageBreakInside: "avoid" }}>
					<div
						style={{
							fontWeight: "600",
							marginBottom: "6px",
							fontSize: "11px",
							color: "#374151",
						}}
					>
						Zahnschema
					</div>
					<div
						style={{
							paddingLeft: "16px",
							backgroundColor: "#f9fafb",
							padding: "10px",
							borderRadius: "4px",
							border: "1px solid #e5e7eb",
						}}
					>
						Ausgewählte Zähne:{" "}
						{teethData.map((t) => t.toothNumber).join(", ")}
					</div>
				</div>
			);
		}

		// Show teeth with their values
		return (
			<div style={{ marginBottom: "12px", pageBreakInside: "avoid" }}>
				<div
					style={{
						fontWeight: "600",
						marginBottom: "6px",
						fontSize: "11px",
						color: "#374151",
					}}
				>
					Zahnschema
				</div>
				<div
					style={{
						paddingLeft: "16px",
						backgroundColor: "#f9fafb",
						padding: "10px",
						borderRadius: "4px",
						border: "1px solid #e5e7eb",
					}}
				>
					{teethWithValues.map((tooth, index) => (
						<div
							key={index}
							style={{ marginBottom: "6px", lineHeight: "1.6" }}
						>
							<span
								style={{ fontWeight: "600", color: "#1f2937" }}
							>
								Zahn {tooth.toothNumber}:{" "}
							</span>
							<span style={{ color: "#4b5563" }}>
								{Object.entries(tooth.values)
									.map(([key, val]) => val)
									.join(", ")}
							</span>
						</div>
					))}
				</div>
			</div>
		);
	};

	return (
		<div
			style={{
				fontFamily:
					"-apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif",
				padding: "48px",
				fontSize: "11px",
				lineHeight: "1.7",
				color: "#1f2937",
				backgroundColor: "#ffffff",
			}}
		>
			{/* Header */}
			<div
				style={{
					borderBottom: "3px solid #111827",
					paddingBottom: "20px",
					marginBottom: "32px",
					pageBreakAfter: "avoid",
				}}
			>
				<h1
					style={{
						fontSize: "22px",
						fontWeight: "700",
						margin: "0 0 12px 0",
						color: "#111827",
						letterSpacing: "0.5px",
					}}
				>
					Auftragsanfrage
				</h1>
				{senderOrgName && (
					<div
						style={{
							fontSize: "12px",
							color: "#374151",
							marginBottom: "6px",
							fontWeight: "600",
							display: "grid",
							gridTemplateColumns: "120px 1fr",
							gap: "8px",
						}}
					>
						<span>Von:</span>
						<span>{senderOrgName}</span>
					</div>
				)}
				<div
					style={{
						fontSize: "11px",
						color: "#6b7280",
						display: "grid",
						gridTemplateColumns: "120px 1fr",
						gap: "8px",
					}}
				>
					<span>Eingegangen am:</span>
					<span>
						{content.incoming_order_date
							? new Date(
									content.incoming_order_date
								).toLocaleDateString("de-DE", {
									day: "2-digit",
									month: "2-digit",
									year: "numeric",
									hour: "2-digit",
									minute: "2-digit",
								})
							: "Kein Datum"}
					</span>
				</div>
			</div>

			{/* Form Data */}
			<div style={{ marginTop: "32px" }}>
				{/* Title */}
				<div
					style={{
						marginBottom: "10px",
						display: "grid",
						gridTemplateColumns: "200px 1fr",
						gap: "12px",
						alignItems: "start",
						pageBreakInside: "avoid",
					}}
				>
					<span
						style={{
							fontWeight: "600",
							fontSize: "11px",
							color: "#374151",
						}}
					>
						Betreff:
					</span>
					<span style={{ color: "#1f2937" }}>{content.title}</span>
				</div>

				{/* Description */}
				<div
					style={{
						marginBottom: "10px",
						display: "grid",
						gridTemplateColumns: "200px 1fr",
						gap: "12px",
						alignItems: "start",
						pageBreakInside: "avoid",
					}}
				>
					<span
						style={{
							fontWeight: "600",
							fontSize: "11px",
							color: "#374151",
						}}
					>
						Beschreibung:
					</span>
					<span style={{ color: "#1f2937" }}>
						{content.description}
					</span>
				</div>

				{/* Patient */}
				{content.patient_first_name && content.patient_last_name && (
					<div
						style={{
							marginBottom: "10px",
							display: "grid",
							gridTemplateColumns: "200px 1fr",
							gap: "12px",
							alignItems: "start",
							pageBreakInside: "avoid",
						}}
					>
						<span
							style={{
								fontWeight: "600",
								fontSize: "11px",
								color: "#374151",
							}}
						>
							Patient:
						</span>
						<span style={{ color: "#1f2937" }}>
							{content.patient_first_name}{" "}
							{content.patient_last_name}
							{content.patient_code
								? ` (${content.patient_code})`
								: ""}
						</span>
					</div>
				)}

				{/* Odontogram */}
				{renderOdontogram()}

				{/* Files - list only */}
				{files && files.length > 0 && (
					<div
						style={{
							marginBottom: "10px",
							display: "grid",
							gridTemplateColumns: "200px 1fr",
							gap: "12px",
							alignItems: "start",
							pageBreakInside: "avoid",
						}}
					>
						<span
							style={{
								fontWeight: "600",
								fontSize: "11px",
								color: "#374151",
							}}
						>
							Angehängte Dateien:
						</span>
						<div>
							{files.map((file, index) => (
								<div
									key={index}
									style={{
										color: "#1f2937",
										fontSize: "11px",
										marginBottom: "2px",
									}}
								>
									• {file}
								</div>
							))}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
